import { useEffect, useState } from 'react';

import {
  Field,
  RowFields,
  Widget,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import type { ValidationItems } from '@kanda-libs/ks-component-ts';
import { Button, Text } from '@kanda-libs/ks-design-library';

import WorkTypesForm from 'components/shared/WorkTypesForm';
import WorkTypesSelect from 'components/shared/WorkTypesSelect';

import { AVERGAE_JOB_VALUE_PROPS } from './constants';

import { COMPANY_ADDRESS_FIELD_NAMES } from '../constants';
import { isFutureInsuranceDate, isValidInsuranceDate } from '../helpers';

const INSURANCE_EXPIRY_VALIDATION = {
  validate: {
    value: {
      valid: (value: string) => isValidInsuranceDate(value),
      future: (value: string) => isFutureInsuranceDate(value),
    },
    message: {
      valid: 'Date must be a valid date',
      future: 'Date must be a future date',
    },
  },
};

interface SectionProps {
  title: string;
}

const Section: React.FunctionComponent<React.PropsWithChildren<SectionProps>> =
  function ({ title, children }) {
    return (
      <div className="mt-6">
        <Text text={title} className="text-16-20-em text-neutral-700 mb-3" />
        <div className="flex flex-col gap-y-3">{children}</div>
      </div>
    );
  };

const AddCompanyFields: React.FunctionComponent = function () {
  const { setValue } = useFormContext();
  const companyType = useWatch({ name: 'company.company_type' });
  const isSoleTrader = companyType === 'sole_trader';

  const [noCompany, setNoCompany] = useState(false);

  const tradeBody = useWatch({ name: 'company.company_info.trade_body' });

  const insuranceContent = useWatch({
    name: 'company.company_info.insurance_document.content',
  });
  const showInsuranceExpiry = Boolean(insuranceContent);
  useEffect(() => {
    if (!tradeBody) return;
    if (tradeBody === 'other') {
      setValue('company.company_info.trade_body_name', '');
      return;
    }
    setValue('company.company_info.trade_body_name', tradeBody);
  }, [tradeBody, setValue]);
  useEffect(() => {
    if (tradeBody === 'none') {
      setValue('company.company_info.trade_body_number', '');
    }
  }, [tradeBody, setValue]);

  return (
    <>
      <Section title="Initial user">
        <RowFields>
          <Field.Input
            name="owner.first_name"
            label="First name"
            placeholder="first name"
          />
          <Field.Input
            name="owner.last_name"
            label="Last name"
            placeholder="last name"
          />
        </RowFields>
        <RowFields>
          <Field.Input
            name="owner.email"
            label="Email"
            placeholder="owner@company.com"
          />
          <Field.PhoneNumberInput name="owner.mobile" label="Phone" />
        </RowFields>
      </Section>

      <Section title="Company type">
        <Widget.CompanyCompanyType inline variant="small" />
      </Section>

      {isSoleTrader ? (
        <Section title="Sole trader details">
          <Widget.CompanySoleTraderInfoTradingName />
          <Widget.CompanySoleTraderInfoNationalInsuranceNumber />
          <RowFields>
            <Widget.CompanySoleTraderInfoTradingAddressLine1 label="Trading Address Line 1" />
            <Widget.CompanySoleTraderInfoTradingAddressLine2 label="Trading Address Line 2 (optional)" />
          </RowFields>
          <RowFields>
            <Widget.CompanySoleTraderInfoTradingAddressCity label="Trading Address City/Town" />
            <Widget.CompanySoleTraderInfoTradingAddressCounty label="Trading Address County (optional)" />
          </RowFields>
          <RowFields>
            <Field.Validator
              validation={{
                required: { value: true, message: 'Postcode is required' },
              }}
            >
              <Field.Input
                name="company.sole_trader_info.trading_address.postcode"
                label="Trading Address Postcode"
                placeholder="postcode"
              />
            </Field.Validator>
            <Widget.CompanySoleTraderInfoTradingAddressCountry />
          </RowFields>
        </Section>
      ) : (
        <Section title="Company details">
          {!noCompany ? (
            <Widget.CompanyLimitedCompanyInfoCompanyName
              noCompanyCallback={() => setNoCompany(true)}
              {...COMPANY_ADDRESS_FIELD_NAMES}
            />
          ) : (
            <div className="flex flex-row items-start">
              <div className="flex-1">
                <Widget.CompanyLimitedCompanyInfoCompanyName />
              </div>
              <Button.Text
                id="staff-add-company-search-for-company"
                label="Search for company"
                onClick={() => setNoCompany(false)}
                variant="outline"
                className="mb-auto ml-4 mt-7 max-w-48"
              />
            </div>
          )}
          {noCompany && <Widget.CompanyLimitedCompanyInfoCompanyNumber />}
          <RowFields>
            <Widget.CompanyLimitedCompanyInfoCompanyAddressLine1 label="Company Address Line 1" />
            <Widget.CompanyLimitedCompanyInfoCompanyAddressLine2 label="Company Address Line 2 (optional)" />
          </RowFields>
          <RowFields>
            <Widget.CompanyLimitedCompanyInfoCompanyAddressCity label="Company Address City/Town" />
            <Widget.CompanyLimitedCompanyInfoCompanyAddressCounty label="Company Address County (optional)" />
          </RowFields>
          <RowFields>
            <Field.Validator
              validation={{
                required: { value: true, message: 'Postcode is required' },
              }}
            >
              <Field.Input
                name="company.limited_company_info.company_address.postcode"
                label="Company Address Postcode"
                placeholder="postcode"
              />
            </Field.Validator>
            <Widget.CompanyLimitedCompanyInfoCompanyAddressCountry />
          </RowFields>
        </Section>
      )}

      <Section title="Regulation">
        <Widget.CompanyFcaNumber />
      </Section>

      <Section title="Business & work types">
        <WorkTypesForm />
        <WorkTypesSelect
          name="company.company_info.work_types"
          title="Select the company's work types"
          alwaysActive
        />
      </Section>

      <Section title="Trade body">
        <Widget.CompanyCompanyInfoTradeBody placeholder="Select..." />
        {tradeBody === 'other' && <Widget.CompanyCompanyInfoTradeBodyName />}
        {tradeBody && tradeBody !== 'none' && (
          <Widget.CompanyCompanyInfoTradeBodyNumber placeholder="Trade body number" />
        )}
      </Section>

      <Section title="Job info">
        <Widget.CompanyCompanyInfoWarrantyLength />
        <RowFields>
          <Widget.CompanyCompanyInfoAverageJobValue
            {...AVERGAE_JOB_VALUE_PROPS}
          />
          <Widget.CompanyCompanyInfoAverageMonthlyJobs />
        </RowFields>
        <Widget.CompanyCompanyInfoUseSubcontractor />
      </Section>

      <Section title="Online presence">
        <Widget.CompanyCompanyInfoOnlinePresenceQOnline />
        <Widget.CompanyCompanyInfoOnlinePresenceWebsite />
        <Widget.CompanyCompanyInfoOnlinePresenceGoogle />
        <Widget.CompanyCompanyInfoOnlinePresenceFacebook />
      </Section>

      <Section title="Insurance">
        <Text
          text="Optional — leave empty to use a placeholder. If you upload a document, the expiry date is required and must be a future date."
          className="text-12-18 text-neutral-600"
        />
        <Widget.CompanyCompanyInfoInsuranceDocumentContent label="Upload public liability insurance (optional)" />
        {showInsuranceExpiry && (
          <Widget.CompanyCompanyInfoInsuranceDocumentExpiry
            label="Insurance expiry date"
            validation={INSURANCE_EXPIRY_VALIDATION as unknown as ValidationItems}
          />
        )}
      </Section>
    </>
  );
};

export default AddCompanyFields;
